import React from "react";
import { Box, Typography } from "@mui/material";
import { BilletGroup, GroupTotal } from "../../../models";
import { BilletItem } from "./BilletItem";
import { sortGroupsByPosition } from "../../../utils";

interface HeatGroupProps {
  heatNumber: string;
  groups: BilletGroup[];
  searchMatchingGroupIds: number[];
  groupTotal?: GroupTotal;
}

export const HeatGroup = React.memo(
  ({ groups, searchMatchingGroupIds, groupTotal }: HeatGroupProps) => {
    const sortedGroups = React.useMemo(
      () => sortGroupsByPosition(groups),
      [groups],
    );

    return (
      <Box sx={{ marginBottom: "8px" }}>
        {/* Group Header - Gray background showing the GroupName */}
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.grey[200],
            color: (theme) => theme.palette.text.primary,
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "1px",
            minHeight: "auto",
            padding: "6px 4px",
            fontSize: "12px",
            borderRadius: "3px 3px 0 0",
          }}
        >
          {groups[0]?.GroupName || "Unknown Group"}
        </Box>

        {/* Individual Billet Details - Each billet as a separate row */}
        {sortedGroups.map((group: BilletGroup, index: number) => (
          <BilletItem
            key={`${group.BilletsGroupID}-${group.Position}`}
            group={group}
            isSearchMatch={searchMatchingGroupIds.includes(
              group.BilletsGroupID,
            )}
            isFirst={index === 0}
            isLast={!groupTotal && index === sortedGroups.length - 1}
          />
        ))}

        {/* Group Total Row - Shows sum of tons and billets for this group */}
        {groupTotal && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: (theme) => theme.palette.grey[100],
              borderTop: (theme) => `1px solid ${theme.palette.grey[300]}`,
              padding: "4px 6px",
              borderRadius: "0 0 3px 3px",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: (theme) => theme.palette.warning.dark,
              }}
            >
              {groupTotal.TotalWeight.toFixed(1)} tons
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: (theme) => theme.palette.grey[700],
                minWidth: "20px",
                textAlign: "right",
              }}
            >
              {groupTotal.TotalBillets}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

HeatGroup.displayName = "HeatGroup";
